import { computedFrom } from "aurelia-binding";
import { bindable } from "aurelia-templating";
import * as Enumerable from "linq";
import { transient, autoinject } from "aurelia-framework";
import { LookupValue, LookupValueResponse, LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { Guid } from "../../../../utilities/guid";
import { Reflection } from "../../../../utilities/reflection";
import { LookupControl } from "../../../components/lookups/lookup-control/lookup-control";

@autoinject
@transient()
export class FormLookupControl {

    private _count: number = 0;
    public isAttached: boolean;
    public itemSource: LookupValue[] = null;

    @bindable
    public parameters: string = "";

    @bindable
    public placeholder: string = "";

    @bindable
    public noItemOptionLabel: string = null;

    @bindable
    public readonly: boolean;

    @bindable
    public info: string = "";

    @bindable
    public disabled: boolean;

    @bindable
    public cacheFlagKey: string = "";

    @bindable
    public label: string;

    @bindable
    public selectDefault: boolean;

    @bindable
    public query: string;

    @bindable
    public hideIfNoData: boolean = false;

    @bindable
    public selectedItem: LookupValue;

    @bindable
    public excludeItemsById: string | number | any[] = [];

    @bindable
    public valueConverter: any;

    @bindable
    public selectedValue: number;

    @bindable
    public showOnAdorner: boolean;

    @bindable
    public tab: number;

    @computedFrom(Reflection.getPropertyPath<LookupControl>(x => x.itemSource),
        Reflection.getPropertyPath<LookupControl>(x => x.selectedValue),
        Reflection.getPropertyPath<LookupControl>(x => x.hideIfNoData)
    )
    public get itemSourceFiltered() {
        const excludedIds = LookupControl.filterIds(this.excludeItemsById);

        let filtered: LookupValue[];

        if (this.itemSource) {
            filtered = this.itemSource;

            if (this.itemSource.length > 0 && excludedIds.length > 0) {
                filtered = filtered.filter(x => !excludedIds.some(y => y == x.id) || x.id == this.selectedValue);
            }
        } else {
            filtered = null;
        }

        if (filtered) {
            filtered = filtered.filter(x => x.isActive || x.id == this.selectedValue);
        }

        return (!filtered || (this.hideIfNoData && filtered.length == 0)) ? null : filtered;
    }

    constructor(private element: Element, protected lookupsClientService: LookupClientService) {
    }

    selectItem(item) {
        this.selectedItem = item;
        this.selectedValue = item.id;
    }

    noItemOptionLabelChanged(newValue: string) {
        if (this.itemSource && newValue && !this.itemSource.some(x => x.id == null && x.name == this.noItemOptionLabel)) {
            this.itemSource.unshift({
                id: null,
                name: this.noItemOptionLabel,
                isActive: true,
                isDefault: true,
                isSelectable: true,
                guid: Guid.generateGuid(),
                additionalData: null,
                children: null
            });
            if (!this.selectedValue) {
                this.selectedItem = this.itemSource[0];
            }
        }
    }

    selectedValueChanged(newValue: any): void {
        setTimeout(() => {
            const valueChangedEvent = new CustomEvent("selected-value-changed", {
                detail: { value: newValue },
                bubbles: true
            });
            this.element.dispatchEvent(valueChangedEvent);
        });
    }

    selectedItemChanged(newValue: any): void {
        if (this.itemSource) {
            setTimeout(() => {
                const itemChangedEvent = new CustomEvent("selected-item-changed", {
                    detail: { item: newValue },
                    bubbles: true
                });
                this.element.dispatchEvent(itemChangedEvent);
            });
        }
    }

    public queryChanged(newValue: string, oldValue: string): void {
        this.lookupsClientService.unsubscribeForLookupsRefresh(oldValue, this.parameters, this.loadData);
        this.lookupsClientService.subscribeForLookupsRefresh(newValue, this.parameters, this.loadData);
        this.loadData();
    }

    public parametersChanged(newValue: string, oldValue: string): void {
        this.lookupsClientService.unsubscribeForLookupsRefresh(this.query, oldValue, this.loadData);
        this.lookupsClientService.subscribeForLookupsRefresh(this.query, newValue, this.loadData);
        this.loadData();
    }

    public readonlyChanged(): void {
        this.loadData();
    }

    public disabledChanged(): void {
        this.loadData();
    }

    attached(): void {
        this.isAttached = true;
        this.loadData();
    }

    protected loadData = (): void => {
        if (!this.isAttached) return;
        this._count++;
        const localCount = this._count;
        if (this.query) {
            const lookupValueRequest = this.createLookupValueRequest();
            this.lookupsClientService.getLookup(lookupValueRequest, this.cacheFlagKey)
                .then((response: LookupValueResponse) => {
                    if (localCount !== this._count) {
                        return;
                    }
                    if (response) {
                        let cloneResponse = response.allValuesIncludingInactive.slice(0);
                        if (this.selectDefault) {
                            cloneResponse = Enumerable.from(cloneResponse).orderByDescending(x => x.isDefault).toArray();
                        }
                        if (this.noItemOptionLabel) {
                            const emptyLookup = {
                                id: null, name: this.noItemOptionLabel,
                                isActive: true, isDefault: true, isSelectable: true,
                                guid: Guid.generateGuid(), additionalData: null, children: null
                            };
                            cloneResponse.unshift(emptyLookup);
                        }
                        this.itemSource = this.valueConverter ? this.valueConverter(cloneResponse) : cloneResponse;
                    } else {
                        this.itemSource = [];
                    }
                    setTimeout(() => {
                        const valuesLoadedEvent = new CustomEvent("values-loaded", { detail: { values: this.itemSource.slice(0) }, bubbles: true });
                        this.element.dispatchEvent(valuesLoadedEvent);
                    });
                }).catch((e) => {
                    if (localCount !== this._count) {
                        return;
                    }
                    console.error(e);
                    this.itemSource = [];
                });
        }
    }

    protected createLookupValueRequest(): LookupValueRequest {
        const request = new LookupValueRequest();
        request.lookupValueQuery = this.query;
        request.parameters = this.parameters;
        return request;
    }

    detached() {
        this.lookupsClientService.unsubscribeForLookupsRefresh(this.query, this.parameters, this.loadData);
    }
}
